import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { Action } from '../../actions';
import {
    gridChangeAdjustmentData,
    gridFetchAdjustmentInfo,
    switchAdjustmentVisibility,
    updateAdjustmentForecast
} from '../../actions/grid-screen';
import { updatePhraseRemote } from '../../actions/phrases-grid';
import { PhraseAdjustmentItem } from '../../components/PhraseAdjustment/PhraseAdjustment';
import { PhraseEditPopup, PhraseEditPopupProps } from '../../components/PhraseEditPopup/PhraseEditPopup';
import { selectGridSourceParams } from '../../selectors/gridSourceParams/gridSourceParams';
import { getEditCellKey } from '../../service/GridHelpers/GridHelpers';
import { mapStringToPhraseWithMinusWords } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { GroupType } from '../../typings/domain';
import { State } from '../../typings/state';

interface OwnProps {
    phraseId: string;
    campaignId: string;
    groupId: string;
    groupType: GroupType;
    columnName: string;
    value: string;
    onCancel: () => void;
    onChange: (value: string) => void;
    anchor: React.ReactNode;
    regionIds: number[];
    groupMinusKeywords: string[];
}

interface StateProps {
    adjustmentData: {
        visible: boolean;
        showsForecast?: number;
        hasMoreItems: boolean;
        items?: PhraseAdjustmentItem[];
    };
    isInProgress: boolean;
    error?: string;
    isGridScrolling: boolean;
}

interface DispatchProps {
    updateAdjustmentForecast: (value: string) => void;
    switchAdjustmentVisibility: (visible: boolean) => void;
    loadForecastData: (phraseWithMinusWords: string) => void;
    onAdjustmentWillUnmount: () => void;
    gridChangeAdjustmentData: (items: PhraseAdjustmentItem[]) => void;
    onSave: () => void;
}

type MergedProps = PhraseEditPopupProps;

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
    const { editCellsData, isGridScrolling } = state.grid;
    const { phraseId, columnName } = ownProps;
    const { view } = selectGridSourceParams(state);
    const key = getEditCellKey({
        id: phraseId,
        column: columnName,
        view
    });
    const isInProgress = editCellsData[key] ? editCellsData[key].isSaving : false;
    const error = editCellsData[key] ? (editCellsData[key].error as string) : undefined;

    const { phraseAdjustment } = state.grid;

    const { minusWords } = mapStringToPhraseWithMinusWords(ownProps.value);

    let items = phraseAdjustment.items;

    if (phraseAdjustment.items) {
        items = phraseAdjustment.items.map(el => ({
            ...el,
            checked: minusWords.includes(el.minusWord)
        }));
    }

    return {
        adjustmentData: {
            ...phraseAdjustment,
            items
        },
        isInProgress,
        error,
        isGridScrolling
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps): DispatchProps => {
    return {
        onSave: () => {
            const { phrase, minusWords } = mapStringToPhraseWithMinusWords(ownProps.value);

            dispatch(
                updatePhraseRemote(
                    {
                        id: ownProps.phraseId,
                        groupId: ownProps.groupId,
                        phrase,
                        minusWords
                    },
                    ownProps.columnName
                )
            );
        },
        updateAdjustmentForecast: (value: string) => {
            dispatch(
                updateAdjustmentForecast({
                    phrase: value,
                    minusWords: ownProps.groupMinusKeywords,
                    geo: ownProps.regionIds,
                    groupId: ownProps.groupId,
                    campaignId: ownProps.campaignId,
                    groupType: ownProps.groupType
                })
            );
        },
        switchAdjustmentVisibility: (visible: boolean) => {
            dispatch(switchAdjustmentVisibility(visible));
        },
        loadForecastData: phraseWithMinusWords => {
            dispatch(
                gridFetchAdjustmentInfo({
                    phrase: phraseWithMinusWords,
                    minusWords: ownProps.groupMinusKeywords,
                    geo: ownProps.regionIds,
                    groupType: ownProps.groupType
                })
            );
        },
        onAdjustmentWillUnmount: () => {
            dispatch(switchAdjustmentVisibility(false));
        },
        gridChangeAdjustmentData: (items: PhraseAdjustmentItem[]) => {
            dispatch(
                gridChangeAdjustmentData({
                    items
                })
            );
        }
    };
};

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): MergedProps => {
    const { groupMinusKeywords, phraseId, groupId, columnName, ...realOwnProps } = ownProps;

    return {
        ...realOwnProps,
        ...stateProps,
        ...dispatchProps
    };
};

export const PhraseEditPopupContainer = connect<StateProps, DispatchProps, OwnProps, MergedProps>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(PhraseEditPopup);
